import { useTranslation } from 'react-i18next';
import { useItensOS, useServicosAtivos } from '@/hooks/useServicos';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter,
} from '@/components/ui/table';
import { formatarValor } from '@/types/servico';
import { formatarTempo } from './finalizar-os-logic';
import { valorItemLive } from '@/lib/os-valor-live';
import { useMinutosExecucao } from '@/hooks/useMinutosExecucao';
import type { StatusOrdemServico } from '@/types/ordemServico';
import { Package, Loader2 } from 'lucide-react';

interface ItensOSDisplayProps {
  ordemServicoId: string;
  valorTotal?: number;
  status: StatusOrdemServico;
  dataInicioExecucao?: string | null;
}

export function ItensOSDisplay({ ordemServicoId, valorTotal, status, dataInicioExecucao }: ItensOSDisplayProps) {
  const { t } = useTranslation();
  const { data: itens, isLoading } = useItensOS(ordemServicoId);
  const { data: servicos } = useServicosAtivos();
  const minutosDecorridos = useMinutosExecucao(dataInicioExecucao, status);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!itens || itens.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Package className="h-4 w-4" />
            {t('common.services')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">{t('orders.details.noDetailedServices')}</p>
          {valorTotal !== undefined && valorTotal > 0 && (
            <div className="mt-3 pt-3 border-t">
              <div className="flex justify-between items-center">
                <span className="font-medium">{t('orders.details.totalValue')}</span>
                <span className="text-lg font-bold text-primary">{formatarValor(valorTotal)}</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  const tipoPrecoDe = (servicoId: string) => servicos?.find((s) => s.id === servicoId)?.tipo_preco;

  const linhas = itens.map((item) => {
    const tipoPreco = tipoPrecoDe(item.servico_id);
    const valor = valorItemLive({
      status,
      tipoPreco,
      taxa: item.valor_unitario,
      valorPersistido: item.valor_total,
      minutosDecorridos,
    });
    let qtd: string;
    if (tipoPreco === 'por_hora') {
      if (status === 'em_execucao') qtd = formatarTempo(minutosDecorridos);
      else if (status === 'pendente' || status === 'aceita') qtd = '—';
      else qtd = formatarTempo(Math.round(item.quantidade * 60));
    } else {
      qtd = String(item.quantidade);
    }
    return { item, valor, qtd };
  });

  const totalCalculado = linhas.reduce((acc, l) => acc + l.valor, 0);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{t('orders.details.contractedServices')}</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('orders.details.serviceHeader')}</TableHead>
              <TableHead className="text-center">{t('orders.details.qtyHeader')}</TableHead>
              <TableHead className="text-right">{t('orders.details.unitValueHeader')}</TableHead>
              <TableHead className="text-right">{t('orders.details.subtotalHeader')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {linhas.map(({ item, valor, qtd }) => (
              <TableRow key={item.id}>
                <TableCell className="font-medium">{item.servico_nome}</TableCell>
                <TableCell className="text-center">{qtd}</TableCell>
                <TableCell className="text-right text-muted-foreground">{formatarValor(item.valor_unitario)}</TableCell>
                <TableCell className="text-right font-medium">{formatarValor(valor)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell colSpan={3} className="text-right font-semibold">{t('common.total')}</TableCell>
              <TableCell className="text-right text-lg font-bold text-primary">{formatarValor(totalCalculado)}</TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </CardContent>
    </Card>
  );
}
